import { useNavigateToAdvancedEditor } from '@/hooks/navigation';
import { useTiming } from '@/hooks/useTiming';
import { PolotnoStoreKeys } from '@/pages/AdvancedEditor/hooks';
import useGetProject from '@/pages/AdvancedEditor/hooks/useGetProject';
import { newProjectId } from '@/pages/AdvancedEditor/types';
import { getProjectScenes } from '@/services/api';
import { useAuth } from '@/services/auth';
import { logger } from '@/services/logger';
import { parseRawStore } from '@/services/polotno-parser';
import { Providers, ScenePresenter, ScriptType, VoiceConfig } from '@d-id/types';
import { AxiosError } from 'axios';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useProjectIdb } from './useProjectIdb';

const getProjectId = (pathname: string): string | null => {
    const projectId = pathname.split('/').find(s => s.startsWith('prj_'));

    return projectId || null;
};

export interface ProjectScript {
    type: ScriptType.text;
    input: string;
    subtitles: boolean | undefined;
    ssml: boolean;
    provider: {
        model_id?: string | undefined;
        voice_config?: VoiceConfig | undefined;
        type: Providers;
        voice_id: string;
        voice_language: string;
    };
}

export interface ProjectInitialData {
    rawData: any;
    videoName: string;
    presenter: ScenePresenter | undefined;
    script: ProjectScript;
    audio: {
        duration: number | undefined;
        audio_url: string | undefined;
    } | null;
}

const getPresenterAndScript = async (projectId: string, rawData: ReturnType<typeof parseRawStore>) => {
    if (rawData?.custom) {
        // Not needed for custom projects
        return { presenter: undefined, script: undefined };
    }

    const scenes = await getProjectScenes(projectId);
    return { presenter: scenes?.[0]?.presenter, script: scenes?.[0]?.script };
};

export const useGetPolotnoInitialData = () => {
    const { isAnonymous } = useAuth();
    const { pathname } = useLocation();
    const { getIdbDraft, clearIdbDraft } = useProjectIdb();
    const { navigateToAdvancedEditor } = useNavigateToAdvancedEditor();
    const { withTiming } = useTiming();
    const projectId = getProjectId(pathname);
    const { data: project } = useGetProject(projectId || '');

    const getDataFromIdb = useCallback(async () => {
        const idbDraft = await getIdbDraft();

        if (idbDraft) {
            if (!isAnonymous) {
                await clearIdbDraft();
            }

            return {
                rawData: parseRawStore(idbDraft.store),
                videoName: idbDraft.videoName,
                presenter: idbDraft.presenters?.[0],
                script: idbDraft.scripts?.[0],
                audio: null,
            };
        }

        return null;
    }, [clearIdbDraft, getIdbDraft, isAnonymous]);

    const getDataFromRemote = useCallback(async () => {
        try {
            if (!project || !projectId) {
                logger.error(`[useGetPolotnoInitialData] No project`, { projectId });
                throw new Error('No project');
            }

            const rawDataUrl = project.raw_data_url;

            if (!rawDataUrl) {
                logger.error(`[useGetPolotnoInitialData] No project.raw_data_url`, { projectId });
                throw new Error('No project.raw_data_url');
            }

            const rawDataJSON = await withTiming('getDataFromRemote.fetch', () => fetch(rawDataUrl).then(res => res.json()), {
                projectId,
            });
            const rawData = parseRawStore(rawDataJSON);

            const { presenter, script } = await withTiming('getPresenterAndScript', () => getPresenterAndScript(projectId, rawData), {
                projectId,
            });

            if (rawData?.custom) {
                rawData.custom[PolotnoStoreKeys.creativeVideoName] = project.name;
            }

            return {
                rawData: Object.keys(rawData)?.length ? rawData : null,
                videoName: project.name,
                presenter: presenter,
                script: script,
                audio: {
                    duration: project.duration,
                    audio_url: project.audio_url,
                },
            };
        } catch (error) {
            if (error instanceof AxiosError && error.status === 404) {
                logger.warn(`[useGetPolotnoInitialData] Project not found, redirecting to new project`, { projectId, error });
                await navigateToAdvancedEditor({ projectId: undefined });
                return null;
            }

            logger.error(`[useGetPolotnoInitialData] Failed to fetch project data`, { projectId, error });
            throw error;
        }
    }, [navigateToAdvancedEditor, project, projectId, withTiming]);

    const getInitialData = useCallback(
        async (prioritizeIdb = false): Promise<ProjectInitialData | null> => {
            // Getting data from local idb
            if (projectId === newProjectId) {
                return withTiming('useGetPolotnoInitialData.getDataFromIdb', () => getDataFromIdb());
            }

            if (prioritizeIdb) {
                const idbData = await withTiming('useGetPolotnoInitialData.getDataFromIdb', () => getDataFromIdb());
                if (idbData) {
                    return idbData;
                }
            }

            // Getting data from remote db
            if (projectId) {
                return withTiming('useGetPolotnoInitialData.getDataFromRemote', () => getDataFromRemote());
            }

            return null;
        },
        [getDataFromIdb, getDataFromRemote, projectId, withTiming]
    );

    return { getInitialData };
};
